import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { jobCardsAPI, machinesAPI } from '../services/api';
import { boringStage } from '../utils/stages';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import appLogo from '../assets/logo.png';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import NotificationBell from './NotificationBell';
import {
  LogOut,
  Scissors,
  Layers,
  CircleDot,
  CheckCircle,
  History,
  Play,
  Clock,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  ImageIcon,
  Printer,
  Eye,
  Search,
  X
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { printJobCard } from '../utils/printUtils';
import PressingBalance from './PressingBalance';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Status options
const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started', color: 'bg-gray-100 text-gray-800' },
  { value: 'started', label: 'Started', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800' }
];

const OperatorDashboard = () => {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [selectedJobCard, setSelectedJobCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [viewingPressingImage, setViewingPressingImage] = useState(null);
  const [viewingPressingSheet, setViewingPressingSheet] = useState(false);
  
  // Job cards for this operator's role across all projects (drives the pending/done picker)
  const [roleJobCards, setRoleJobCards] = useState([]);
  const [showPendingOnly, setShowPendingOnly] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [coordinatorFilter, setCoordinatorFilter] = useState('all');

  // For boring operator
  const [boringQuantity, setBoringQuantity] = useState('');
  const [boringNote, setBoringNote] = useState('');
  
  // For cutting operator
  const [cuttingNote, setCuttingNote] = useState('');
  const [cuttingQuantity, setCuttingQuantity] = useState('');
  const [cuttingMachineNumber, setCuttingMachineNumber] = useState('');
  const [cuttingMachineId, setCuttingMachineId] = useState('');
  const [cuttingMachines, setCuttingMachines] = useState([]);
  const [workLogs, setWorkLogs] = useState(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Cutting operators: load active cutting machines for dropdown
    if (user?.role === 'cutting_operator') {
      machinesAPI.getAll({ category: 'cutting', active_only: true })
        .then(setCuttingMachines)
        .catch(() => setCuttingMachines([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // One flat list of this operator's job cards - there is no project step,
      // since in practice each project carries exactly one job card.
      const allCards = await jobCardsAPI.getAll();
      setRoleJobCards((allCards || []).filter(isCardForMyRole));
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Job cards this operator is responsible for (same rule as the per-project fetch)
  const isCardForMyRole = (jc) => {
    const validStatuses = ['in_production', 'in_progress'];
    if (!validStatuses.includes(jc.status)) return false;
    if (user?.role === 'machine_operator') return jc.card_type === 'combined' || jc.card_type === 'pressing';
    if (user?.role === 'cutting_operator') return jc.card_type === 'combined' || jc.card_type === 'cutting';
    if (user?.role === 'boring_operator') return jc.card_type === 'combined' || jc.card_type === 'work_details';
    return true;
  };

  // Keep the picker's pending/done dots in step with the card we just updated
  const syncRoleJobCard = (updated) => {
    if (!updated?.id) return;
    setRoleJobCards(prev => prev.map(jc => (jc.id === updated.id ? updated : jc)));
  };

  // Has this operator finished their part of the card?
  const isCardDone = (jc) => {
    // Boring spans days too: finished only when the operator taps Finish.
    if (user?.role === 'boring_operator') return boringStage(jc) === 'completed';
    // Cutting spans days. A card is finished only when the operator says so -
    // entering a quantity used to mark it done, which made a part-cut card
    // disappear from the pending list before the work was actually finished.
    if (user?.role === 'cutting_operator') {
      return jc.cutting_status === 'completed';
    }
    if (user?.role === 'machine_operator') {
      const lines = [
        ...(jc.normal_pressing_lines || []),
        ...(jc.hot_pressing_lines || []),
        ...(jc.veneer_pressing_lines || []),
      ];
      return lines.length > 0 && lines.every(l => l.status === 'completed');
    }
    return false;
  };

  const coordinators = React.useMemo(() => {
    const names = [...new Set(roleJobCards.map(jc => jc.created_by_name).filter(Boolean))];
    return names.sort((a, b) => a.localeCompare(b));
  }, [roleJobCards]);

  const visibleJobCards = React.useMemo(() => {
    // A search spans every card - including completed ones - so an operator can
    // always find a finished entry to correct. "Pending only" is suspended while
    // searching (the toggle greys out to show this). With no query, it applies.
    const searching = searchQuery.trim().length > 0;
    let list = (showPendingOnly && !searching) ? roleJobCards.filter(jc => !isCardDone(jc)) : roleJobCards;
    if (coordinatorFilter !== 'all') {
      list = list.filter(jc => jc.created_by_name === coordinatorFilter);
    }
    if (searching) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(jc =>
        (jc.job_card_number || '').toLowerCase().includes(q) ||
        (jc.customer_name || '').toLowerCase().includes(q) ||
        (jc.created_by_name || '').toLowerCase().includes(q) ||
        (jc.reference || '').toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      const ad = isCardDone(a) ? 1 : 0;
      const bd = isCardDone(b) ? 1 : 0;
      if (ad !== bd) return ad - bd;
      return (b.job_card_number || '').localeCompare(a.job_card_number || '', undefined, { numeric: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleJobCards, showPendingOnly, coordinatorFilter, searchQuery, user?.role]);

  // Select a card and seed the fields this operator's panel edits
  const selectJobCard = (jobCard) => {
    setSelectedJobCard(jobCard || null);
    if (!jobCard) return;
    if (user?.role === 'boring_operator') {
      setBoringQuantity(jobCard.boring_qty_total?.toString() || '');
    }
    if (user?.role === 'cutting_operator') {
      setCuttingNote(jobCard.cutting_operator_note || '');
      setCuttingQuantity(jobCard.cutting_qty_total?.toString() || '');
      setCuttingMachineNumber(jobCard.cutting_machine_number || '');
      setCuttingMachineId(jobCard.cutting_machine_id || '');
    }
  };

  const handleSelectJobCard = (jobCardId) => {
    selectJobCard(roleJobCards.find(jc => jc.id === jobCardId));
  };

  // Per-day entry history for the open card, so the operator can see what they
  // already booked instead of having to remember or re-add it.
  const loadWorkLogs = async (jobCardId) => {
    if (!jobCardId) { setWorkLogs([]); return; }
    setWorkLogs(null);
    try {
      const logs = await jobCardsAPI.getWorkLogs(jobCardId);
      setWorkLogs(Array.isArray(logs) ? logs : []);
    } catch { setWorkLogs([]); }
  };

  useEffect(() => {
    if (selectedJobCard?.id) loadWorkLogs(selectedJobCard.id);
    else setWorkLogs(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJobCard?.id]);

  // When the list narrows to exactly one card - whether that's the only
  // pending card on load, or the result of a search/filter - open it
  // automatically so the operator doesn't have to tap the dropdown.
  // The id guard stops it re-selecting (and wiping typed-but-unsaved fields)
  // on every render once that card is already open.
  useEffect(() => {
    if (visibleJobCards.length === 1 && selectedJobCard?.id !== visibleJobCards[0].id) {
      selectJobCard(visibleJobCards[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleJobCards]);

  const refreshJobCard = async () => {
    if (selectedJobCard) {
      try {
        const updated = await jobCardsAPI.getById(selectedJobCard.id);
        setSelectedJobCard(updated);
      syncRoleJobCard(updated);
        toast({ title: "Refreshed", description: "Job card data updated" });
      } catch (error) {
        toast({ title: "Error", description: "Failed to refresh", variant: "destructive" });
      }
    }
  };

  // ===========================
  // PRESSING OPERATOR FUNCTIONS
  // ===========================
  const handleLineStatusChange = async (lineType, lineId, newStatus, note = null) => {
    if (!selectedJobCard) return;
    setUpdating(true);
    try {
      await jobCardsAPI.updatePressingLineStatus(selectedJobCard.id, lineType, lineId, newStatus, note);
      toast({ title: "Success", description: "Line status updated" });
      // Refresh job card
      const updated = await jobCardsAPI.getById(selectedJobCard.id);
      setSelectedJobCard(updated);
      syncRoleJobCard(updated);
    } catch (error) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    } finally {
      setUpdating(false);
    }
  };

  // ===========================
  // CUTTING OPERATOR FUNCTIONS
  // ===========================
  const handleCuttingStatusChange = async (newStatus) => {
    if (!selectedJobCard) return;
    setUpdating(true);
    try {
      await jobCardsAPI.updateCuttingStatus(selectedJobCard.id, newStatus, cuttingNote || null);
      toast({ title: "Success", description: "Cutting status updated" });
      // Refresh job card
      const updated = await jobCardsAPI.getById(selectedJobCard.id);
      setSelectedJobCard(updated);
      syncRoleJobCard(updated);
    } catch (error) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    } finally {
      setUpdating(false);
    }
  };

  const handleCuttingQuantitySubmit = async () => {
    if (!selectedJobCard) return;
    if (!cuttingMachineId) {
      toast({ title: "Error", description: "Please select a machine", variant: "destructive" });
      return;
    }
    const selectedMachine = cuttingMachines.find(m => m.id === cuttingMachineId);
    const machineNumber = selectedMachine?.machine_number || cuttingMachineNumber;
    const machineName = selectedMachine?.name || null;

    setUpdating(true);
    try {
      // The operator enters the running cutting total for the card. (An
      // "add today's cutting" flow is built but held back until we split the
      // coordinator's cutting target from the operator's actual - otherwise a
      // coordinator-typed target could be added on top of the operator's work.)
      await jobCardsAPI.updateCuttingMachineNumber(
        selectedJobCard.id,
        machineNumber,
        parseInt(cuttingQuantity) || 0,
        cuttingMachineId,
        machineName
      );
      
      // Update cutting status and note if provided
      if (cuttingNote) {
        await jobCardsAPI.updateCuttingStatus(selectedJobCard.id, selectedJobCard.cutting_status || 'not_started', cuttingNote);
      }
      
      toast({ title: "Saved", description: `Cutting total set to ${parseInt(cuttingQuantity) || 0}` });
      // Refresh job card
      const updated = await jobCardsAPI.getById(selectedJobCard.id);
      setSelectedJobCard(updated);
      syncRoleJobCard(updated);
      setCuttingQuantity((updated.cutting_qty_total ?? 0).toString());
      loadWorkLogs(selectedJobCard.id);
    } catch (error) {
      toast({ title: "Error", description: "Failed to save cutting details", variant: "destructive" });
    } finally {
      setUpdating(false);
    }
  };

  // ===========================
  // BORING OPERATOR FUNCTIONS
  // ===========================
  const handleFinishCutting = async (finish) => {
    if (!selectedJobCard) return;
    setUpdating(true);
    try {
      await jobCardsAPI.updateCuttingStatus(selectedJobCard.id, finish ? 'completed' : 'started', cuttingNote || null);
      const updated = await jobCardsAPI.getById(selectedJobCard.id);
      setSelectedJobCard(updated);
      syncRoleJobCard(updated);
      toast({
        title: finish ? "Cutting finished" : "Reopened",
        description: finish ? "This card is now marked complete." : "The card is back in your pending list.",
      });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update cutting status", variant: "destructive" });
    } finally {
      setUpdating(false);
    }
  };

  const handleFinishBoring = async (finish) => {
    if (!selectedJobCard) return;
    setUpdating(true);
    try {
      await jobCardsAPI.updateBoringStatus(selectedJobCard.id, finish ? 'completed' : 'started');
      const updated = await jobCardsAPI.getById(selectedJobCard.id);
      setSelectedJobCard(updated);
      syncRoleJobCard(updated);
      toast({
        title: finish ? "Boring finished" : "Reopened",
        description: finish ? "This card is now marked complete." : "The card is back in your pending list.",
      });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update boring status", variant: "destructive" });
    } finally {
      setUpdating(false);
    }
  };

  const handleBoringQuantitySubmit = async () => {
    if (!selectedJobCard || !boringQuantity) return;
    setUpdating(true);
    try {
      await jobCardsAPI.updateBoringQuantity(selectedJobCard.id, parseInt(boringQuantity), boringNote || null);
      toast({ title: "Success", description: "Boring quantity updated" });
      // Refresh job card
      const updated = await jobCardsAPI.getById(selectedJobCard.id);
      setSelectedJobCard(updated);
      syncRoleJobCard(updated);
      setBoringNote('');
    } catch (error) {
      toast({ title: "Error", description: "Failed to update quantity", variant: "destructive" });
    } finally {
      setUpdating(false);
    }
  };

  const getOperatorIcon = () => {
    switch (user?.role) {
      case 'machine_operator': return <Layers className="w-6 h-6" />;
      case 'cutting_operator': return <Scissors className="w-6 h-6" />;
      case 'boring_operator': return <CircleDot className="w-6 h-6" />;
      default: return <Layers className="w-6 h-6" />;
    }
  };

  const getOperatorTitle = () => {
    switch (user?.role) {
      case 'machine_operator': return 'Pressing Operator';
      case 'cutting_operator': return 'Cutting Operator';
      case 'boring_operator': return 'Boring Operator';
      default: return 'Operator';
    }
  };

  // Print function specifically for Pressing Operator with detailed items and diagram
  const printPressingJobCard = (jc) => {
    const allLines = [
      ...(jc.normal_pressing_lines || []).map(l => ({ ...l, type: 'Normal Pressing' })),
      ...(jc.hot_pressing_lines || []).map(l => ({ ...l, type: 'Normal Pressing' })),
      ...(jc.veneer_pressing_lines || []).map(l => ({ ...l, type: 'PVC & Acrylic Pressing' }))
    ];

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast({ title: "Error", description: "Please allow popups for printing", variant: "destructive" });
      return;
    }

    const pressingImagesHtml = (jc.pressing_images && jc.pressing_images.length > 0) 
      ? `
        <div class="section">
          <h3 style="margin-bottom: 10px; border-bottom: 1px solid #ddd; padding-bottom: 5px;">Pressing Diagrams</h3>
          <div style="display: flex; flex-wrap: wrap; gap: 15px; justify-content: center;">
            ${jc.pressing_images.map((url, idx) => `
              <img src="${API_BASE}${url}" alt="Pressing Diagram ${idx + 1}" 
                style="max-width: 280px; max-height: 280px; border: 1px solid #ddd; border-radius: 4px;" />
            `).join('')}
          </div>
        </div>
      ` : '';

    const linesTableHtml = allLines.length > 0 ? `
      <div class="section">
        <h3 style="margin-bottom: 10px; border-bottom: 1px solid #ddd; padding-bottom: 5px;">Pressing Items</h3>
        <table>
          <thead>
            <tr>
              <th style="width: 40px;">#</th>
              <th>Description</th>
              <th>Type</th>
              <th style="width: 60px;">Ply</th>
              <th style="width: 60px;">Side</th>
              <th style="width: 70px;">Total</th>
              <th style="width: 80px;">Status</th>
            </tr>
          </thead>
          <tbody>
            ${allLines.map((line, idx) => `
              <tr>
                <td style="text-align: center;">${idx + 1}</td>
                <td>${line.description || '-'}</td>
                <td>${line.type}</td>
                <td style="text-align: center;">${line.ply_qty || '-'}</td>
                <td style="text-align: center;">${line.side || '-'}</td>
                <td style="text-align: center; font-weight: bold;">${line.total || (line.ply_qty * line.side) || '-'}</td>
                <td style="text-align: center;">
                  <span style="padding: 2px 6px; border-radius: 4px; font-size: 11px; background: ${line.status === 'completed' ? '#dcfce7' : '#fef3c7'}; color: ${line.status === 'completed' ? '#166534' : '#92400e'};">
                    ${line.status || 'pending'}
                  </span>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    ` : '<p style="text-align: center; color: #666; padding: 20px;">No pressing items added</p>';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Job Card #${jc.job_card_number} - Pressing Details</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 15px; margin-bottom: 20px; }
          .header h1 { margin: 0 0 5px 0; font-size: 24px; }
          .header .subtitle { color: #666; font-size: 14px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 20px; padding: 15px; background: #f9f9f9; border-radius: 8px; }
          .info-item { font-size: 13px; }
          .info-item .label { color: #666; }
          .info-item .value { font-weight: bold; margin-left: 5px; }
          .summary { display: flex; gap: 30px; justify-content: center; padding: 10px; background: #fff3cd; border-radius: 8px; margin-bottom: 20px; }
          .summary-item { text-align: center; }
          .summary-item .number { font-size: 24px; font-weight: bold; color: #333; }
          .summary-item .label { font-size: 12px; color: #666; }
          .section { margin-bottom: 25px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background: #f5f5f5; font-weight: bold; }
          tr:nth-child(even) { background: #fafafa; }
          @media print { 
            body { padding: 10px; }
            .no-print { display: none; }
          }
          .print-btn { 
            position: fixed; top: 10px; right: 10px; 
            padding: 10px 20px; background: #2563eb; color: white; 
            border: none; border-radius: 6px; cursor: pointer; font-size: 14px;
          }
          .print-btn:hover { background: #1d4ed8; }
        </style>
      </head>
      <body>
        <button class="print-btn no-print" onclick="window.print()">Print</button>
        
        <div class="header">
          <h1>Job Card #${jc.job_card_number}</h1>
          <div class="subtitle">Pressing Work Sheet</div>
        </div>
        
        <div class="info-grid">
          <div class="info-item"><span class="label">Customer:</span><span class="value">${jc.customer_name || '-'}</span></div>
          <div class="info-item"><span class="label">Coordinator:</span><span class="value">${jc.created_by_name || '-'}</span></div>
          <div class="info-item"><span class="label">Work In Charge:</span><span class="value">${jc.work_in_charge || '-'}</span></div>
          <div class="info-item"><span class="label">Mobile:</span><span class="value">${jc.mobile_no || '-'}</span></div>
          <div class="info-item"><span class="label">Reference:</span><span class="value">${jc.reference || '-'}</span></div>
          <div class="info-item"><span class="label">Status:</span><span class="value">${jc.status?.replace(/_/g, ' ') || '-'}</span></div>
          <div class="info-item"><span class="label">Date:</span><span class="value">${jc.created_at ? new Date(jc.created_at).toLocaleDateString('en-IN') : '-'}</span></div>
        </div>
        
        <div class="summary">
          <div class="summary-item">
            <div class="number">${allLines.reduce((sum, l) => sum + (l.total || l.ply_qty * l.side || 0), 0)}</div>
            <div class="label">Total Pressing</div>
          </div>
          <div class="summary-item">
            <div class="number">${allLines.filter(l => l.status === 'completed').reduce((sum, l) => sum + (l.total || l.ply_qty * l.side || 0), 0)}</div>
            <div class="label">Completed</div>
          </div>
          <div class="summary-item">
            <div class="number">${allLines.filter(l => l.status !== 'completed').reduce((sum, l) => sum + (l.total || l.ply_qty * l.side || 0), 0)}</div>
            <div class="label">Pending</div>
          </div>
        </div>

        ${linesTableHtml}
        
        ${pressingImagesHtml}
        
        <div style="margin-top: 30px; padding-top: 15px; border-top: 1px solid #ddd; font-size: 11px; color: #999; text-align: center;">
          Printed on ${new Date().toLocaleString('en-IN')}
        </div>
      </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  const getGradient = () => {
    switch (user?.role) {
      case 'machine_operator': return 'from-orange-600 to-red-600';
      case 'cutting_operator': return 'from-blue-600 to-cyan-600';
      case 'boring_operator': return 'from-purple-600 to-pink-600';
      default: return 'from-gray-600 to-gray-700';
    }
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0];
    return <Badge className={option.color}>{option.label}</Badge>;
  };

  // Render pressing lines for Machine Operator
  const renderPressingLines = (lines, lineType, title, bgColor) => {
    if (!lines || lines.length === 0) return null;
    
    const isExpanded = expandedSection === lineType;
    const completedCount = lines.filter(l => l.status === 'completed').length;
    
    return (
      <div className="border rounded-lg overflow-hidden mb-4">
        <button
          className={`w-full p-3 flex items-center justify-between ${bgColor}`}
          onClick={() => setExpandedSection(isExpanded ? null : lineType)}
        >
          <div className="flex items-center gap-3">
            <span className="font-semibold">{title}</span>
            <Badge variant="outline">{completedCount}/{lines.length} completed</Badge>
          </div>
          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        
        {isExpanded && (
          <div className="divide-y">
            {lines.map((line, idx) => (
              <div key={line.id || idx} className="p-3 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-medium">{line.description || `Line ${idx + 1}`}</p>
                    <p className="text-sm text-gray-500">
                      Qty: {line.ply_qty} × {line.side} sides = <strong>{line.total}</strong>
                    </p>
                    {line.details && <p className="text-xs text-gray-400">{line.details}</p>}
                  </div>
                  {getStatusBadge(line.status || 'not_started')}
                </div>
                
                {/* Machine Type Selection for each line */}
                <div className="mb-3 p-2 bg-gray-50 rounded-lg">
                  <p className="text-xs font-medium text-gray-600 mb-2">Machine Type:</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={line.machine_type === 'hot' ? 'default' : 'outline'}
                      className={`h-9 ${line.machine_type === 'hot' ? 'bg-orange-500 hover:bg-orange-600' : 'border-orange-300 text-orange-600 hover:bg-orange-50'}`}
                      onClick={async () => {
                        try {
                          await jobCardsAPI.updatePressingLineStatus(
                            selectedJobCard.id, 
                            lineType, 
                            line.id, 
                            line.status || 'not_started',
                            null,
                            'hot'
                          );
                          toast({ title: "Success", description: "Machine set to HOT" });
                          refreshJobCard();
                        } catch (error) {
                          toast({ title: "Error", description: "Failed to update", variant: "destructive" });
                        }
                      }}
                      disabled={updating}
                    >
                      🔥 HOT
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={line.machine_type === 'cold' ? 'default' : 'outline'}
                      className={`h-9 ${line.machine_type === 'cold' ? 'bg-cyan-500 hover:bg-cyan-600' : 'border-cyan-300 text-cyan-600 hover:bg-cyan-50'}`}
                      onClick={async () => {
                        try {
                          await jobCardsAPI.updatePressingLineStatus(
                            selectedJobCard.id, 
                            lineType, 
                            line.id, 
                            line.status || 'not_started',
                            null,
                            'cold'
                          );
                          toast({ title: "Success", description: "Machine set to COLD" });
                          refreshJobCard();
                        } catch (error) {
                          toast({ title: "Error", description: "Failed to update", variant: "destructive" });
                        }
                      }}
                      disabled={updating}
                    >
                      ❄️ COLD
                    </Button>
                  </div>
                </div>
                
                {/* Status Buttons */}
                <div className="flex gap-2">
                  {STATUS_OPTIONS.map(opt => (
                    <Button
                      key={opt.value}
                      size="sm"
                      variant={(line.status || 'not_started') === opt.value ? 'default' : 'outline'}
                      className={`flex-1 ${(line.status || 'not_started') === opt.value ? 'bg-blue-600' : ''}`}
                      onClick={() => handleLineStatusChange(lineType, line.id, opt.value)}
                      disabled={updating}
                    >
                      {opt.value === 'not_started' && <Clock className="w-3 h-3 mr-1" />}
                      {opt.value === 'started' && <Play className="w-3 h-3 mr-1" />}
                      {opt.value === 'completed' && <CheckCircle className="w-3 h-3 mr-1" />}
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className={`bg-gradient-to-r ${getGradient()} text-white px-4 py-4 sticky top-0 z-50`}>
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center overflow-hidden">
              <img src={appLogo} alt="Interior Job Work" className="w-[60px] h-[60px] object-cover" />
            </div>
            <div>
              <h1 className="text-lg font-bold">{getOperatorTitle()}</h1>
              <p className="text-xs text-white/80">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-white hover:bg-white/20"
              onClick={logout}
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        {/* Job Card Selection */}
        <Card>
          <CardContent className="p-4 space-y-3">
            {/* Search + Filters row */}
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search job card, client, coordinator, reference..."
                  className="pl-9 pr-8 h-10"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Select value={coordinatorFilter} onValueChange={setCoordinatorFilter}>
                  <SelectTrigger className="h-9 text-sm flex-1">
                    <SelectValue placeholder="All Coordinators" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Coordinators</SelectItem>
                    {coordinators.map(name => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  disabled={!!searchQuery.trim()}
                  onClick={() => setShowPendingOnly(v => !v)}
                  className={`text-xs px-2 py-1.5 rounded-full border transition-colors whitespace-nowrap ${
                    searchQuery.trim()
                      ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
                      : showPendingOnly
                        ? 'bg-amber-50 border-amber-300 text-amber-700'
                        : 'bg-gray-50 border-gray-300 text-gray-600'
                  }`}
                  title={searchQuery.trim() ? 'Search looks through every card, including completed ones' : ''}
                  data-testid="toggle-pending-only"
                >
                  {searchQuery.trim() ? 'Searching all' : showPendingOnly ? 'Pending only' : 'Showing all'}
                </button>
              </div>
            </div>

            {/* Job card picker */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-gray-500">{visibleJobCards.length} job card{visibleJobCards.length !== 1 ? 's' : ''}</Label>
                {(searchQuery || coordinatorFilter !== 'all') && (
                  <button
                    type="button"
                    onClick={() => { setSearchQuery(''); setCoordinatorFilter('all'); }}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Clear filters
                  </button>
                )}
              </div>
              <Select value={selectedJobCard?.id || ''} onValueChange={handleSelectJobCard}>
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="Choose a job card" />
                </SelectTrigger>
                <SelectContent>
                  {visibleJobCards.map(jc => {
                    const done = isCardDone(jc);
                    return (
                      <SelectItem key={jc.id} value={jc.id} data-testid={`job-card-option-${jc.job_card_number}`}>
                        <span className="flex items-center gap-2">
                          <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${done ? 'bg-green-500' : 'bg-amber-500'}`} />
                          <span className={done ? 'text-gray-500' : 'font-medium'}>
                            {jc.job_card_number} - {jc.customer_name}
                          </span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                            done ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {done ? 'Done' : 'Pending'}
                          </span>
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {visibleJobCards.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-2">
                  {searchQuery || coordinatorFilter !== 'all'
                    ? 'No job cards match the current filters.'
                    : showPendingOnly
                      ? 'Nothing pending for you right now. Tap "Pending only" to see completed cards.'
                      : 'No job cards assigned to your role.'}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Job Card Actions based on Role */}
        {selectedJobCard && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  Job Card #{selectedJobCard.job_card_number}
                </CardTitle>
                <div className="flex items-center gap-1">
                  {user?.role === 'machine_operator' && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => setViewingPressingSheet(true)} title="View Full Pressing Sheet" data-testid="operator-view-sheet-btn">
                        <Eye className="w-4 h-4 mr-1" /> View
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => printPressingJobCard(selectedJobCard)} title="Print Job Card with Diagram" data-testid="operator-print-btn">
                        <Printer className="w-4 h-4 mr-1" /> Print
                      </Button>
                    </>
                  )}
                  {user?.role !== 'machine_operator' && (
                    <Button variant="ghost" size="sm" onClick={() => printJobCard(selectedJobCard)} title="Print" data-testid="operator-print-btn">
                      <Printer className="w-4 h-4" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={refreshJobCard}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Job Card Details */}
              <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-2">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div>
                    <span className="text-gray-500">Customer:</span>
                    <span className="ml-2 font-medium">{selectedJobCard.customer_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Coordinator:</span>
                    <span className="ml-2 text-blue-600">{selectedJobCard.created_by_name || '-'}</span>
                  </div>
                  {selectedJobCard.work_in_charge && (
                    <div>
                      <span className="text-gray-500">In Charge:</span>
                      <span className="ml-2 font-medium">{selectedJobCard.work_in_charge}</span>
                    </div>
                  )}
                  {selectedJobCard.mobile_no && (
                    <div>
                      <span className="text-gray-500">Mobile:</span>
                      <span className="ml-2 font-medium">{selectedJobCard.mobile_no}</span>
                    </div>
                  )}
                  {selectedJobCard.reference && (
                    <div>
                      <span className="text-gray-500">Reference:</span>
                      <span className="ml-2">{selectedJobCard.reference}</span>
                    </div>
                  )}
                  {selectedJobCard.created_at && (
                    <div>
                      <span className="text-gray-500">Created:</span>
                      <span className="ml-2">{new Date(selectedJobCard.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">Status:</span>
                    <Badge className={`ml-2 ${selectedJobCard.status === 'in_production' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {selectedJobCard.status?.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                </div>
                
                {/* Summary for Pressing Operator */}
                {user?.role === 'machine_operator' && (
                  <div className="mt-2 pt-2 border-t flex gap-4 text-sm">
                    <span className="text-gray-600">
                      <strong className="text-orange-600">{selectedJobCard.pressing_qty_total || 0}</strong> Total Pressing
                    </span>
                    <span className="text-gray-600">
                      <strong className="text-blue-600">{selectedJobCard.cutting_qty_total || 0}</strong> Cutting
                    </span>
                    <span className="text-gray-600">
                      <strong className="text-purple-600">{selectedJobCard.boring_qty_total || 0}</strong> Boring
                    </span>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">

              {/* Already-saved hint: makes editing an entry discoverable */}
              {isCardDone(selectedJobCard) && (
                <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800" data-testid="operator-edit-hint">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-600" />
                  <span>You've already saved this one. Need to fix it? Change the value below and save again to update.</span>
                </div>
              )}

              {/* PRESSING OPERATOR VIEW - Status Update Only (Click "View" button to see full sheet) */}
              {user?.role === 'machine_operator' && (
                <>
                  {/* Update Status Section */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <PressingBalance jobCard={selectedJobCard} variant="full" className="mb-4 pb-4 border-b" />
                    <h4 className="font-semibold mb-3">Update Pressing Status</h4>
                    <p className="text-sm text-gray-600 mb-3">
                      Click <strong>View</strong> above to see the full pressing sheet sent by the Coordinator. Update each line's status below.
                    </p>
                    
                    {renderPressingLines(
                      [...(selectedJobCard.normal_pressing_lines || []), ...(selectedJobCard.hot_pressing_lines || [])], 
                      'normal_pressing', 
                      'Normal Pressing Lines', 
                      'bg-blue-100'
                    )}
                    
                    {renderPressingLines(
                      selectedJobCard.veneer_pressing_lines, 
                      'veneer_pressing', 
                      'PVC & Acrylic Pressing Lines', 
                      'bg-purple-100'
                    )}
                  </div>
                </>
              )}

              {/* CUTTING OPERATOR VIEW */}
              {user?.role === 'cutting_operator' && (
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg">

                    {/* What has been cut so far, and on which days */}
                    <div className="mb-4">
                      <div className="flex items-baseline justify-between">
                        <span className="text-sm text-gray-600">Cut so far</span>
                        {getStatusBadge(selectedJobCard.cutting_status || 'not_started')}
                      </div>
                      <p className="text-4xl font-bold text-blue-700 leading-tight">
                        {selectedJobCard.cutting_qty_total || 0}
                      </p>

                      {workLogs === null ? (
                        <p className="text-xs text-gray-400 mt-2">Loading previous entries...</p>
                      ) : (
                        (() => {
                          const cuts = workLogs
                            .filter(l => l.operation_type === 'cutting')
                            .sort((a, b) => (a.work_day || '').localeCompare(b.work_day || ''));
                          if (cuts.length === 0) {
                            return <p className="text-xs text-gray-400 mt-2">No entries yet - this will be the first.</p>;
                          }
                          return (
                            <div className="mt-2 divide-y border-t border-blue-100" data-testid="cutting-history">
                              {cuts.map(l => (
                                <div key={l.id} className="flex items-center justify-between py-1.5 text-sm">
                                  <span className="text-gray-600">
                                    {l.work_day
                                      ? new Date(l.work_day + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
                                      : '-'}
                                    <span className="text-gray-400"> &middot; {l.operator_name || 'unknown'}</span>
                                  </span>
                                  <span className="font-semibold text-gray-800">
                                    +{l.delta_qty != null ? l.delta_qty : (l.quantity || 0)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          );
                        })()
                      )}
                    </div>

                    {/* Machine */}
                    <div className="space-y-2">
                      <Label className="text-base font-semibold">Machine *</Label>
                      {cuttingMachines.length === 0 ? (
                        <div className="p-3 border border-amber-300 bg-amber-50 rounded text-sm text-amber-800">
                          No active cutting machines have been added yet. Please contact your admin.
                        </div>
                      ) : (
                        <Select
                          value={cuttingMachineId}
                          onValueChange={(v) => {
                            setCuttingMachineId(v);
                            const m = cuttingMachines.find(x => x.id === v);
                            if (m) setCuttingMachineNumber(m.machine_number);
                          }}
                        >
                          <SelectTrigger className="h-12" data-testid="cutting-machine-select">
                            <SelectValue placeholder="Select a cutting machine" />
                          </SelectTrigger>
                          <SelectContent>
                            {cuttingMachines.map(m => (
                              <SelectItem key={m.id} value={m.id} data-testid={`cutting-machine-option-${m.machine_number}`}>
                                <span className="font-mono">{m.machine_number}</span>
                                <span className="ml-2 text-gray-500">- {m.name}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    {/* Cutting total (running). The add-today flow is held back
                        until the coordinator target vs operator actual split. */}
                    <div className="mt-4 space-y-2">
                      <Label>Cutting total (all days)</Label>
                      <Input
                        type="number"
                        value={cuttingQuantity}
                        onChange={(e) => setCuttingQuantity(e.target.value)}
                        placeholder="Total pieces cut so far"
                        className="h-14 text-2xl text-center font-bold"
                        min="0"
                        data-testid="cutting-qty-input"
                      />
                    </div>

                    <div className="mt-4 space-y-2">
                      <Label>Add Note (optional)</Label>
                      <Textarea
                        value={cuttingNote}
                        onChange={(e) => setCuttingNote(e.target.value)}
                        placeholder="Any notes about the cutting work..."
                        rows={2}
                      />
                    </div>

                    <Button
                      className={`w-full h-12 mt-4 bg-gradient-to-r ${getGradient()}`}
                      onClick={handleCuttingQuantitySubmit}
                      disabled={updating || !cuttingMachineId}
                      data-testid="cutting-save-btn"
                    >
                      {updating ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <><CheckCircle className="w-5 h-5 mr-2" /> Save cutting details</>
                      )}
                    </Button>

                    {/* Finish - the card stays in the pending list until this is tapped */}
                    <div className="mt-4 pt-4 border-t border-blue-200">
                      {selectedJobCard.cutting_status === 'completed' ? (
                        <div className="text-center">
                          <p className="text-sm text-green-700 font-medium mb-2">
                            <CheckCircle className="w-4 h-4 inline mr-1" />
                            Cutting finished for this job card
                          </p>
                          <Button
                            variant="outline"
                            className="w-full h-11 border-amber-300 text-amber-700 hover:bg-amber-50"
                            onClick={() => handleFinishCutting(false)}
                            disabled={updating}
                            data-testid="cutting-reopen-btn"
                          >
                            Reopen - more still to cut
                          </Button>
                        </div>
                      ) : (
                        <>
                          <p className="text-xs text-gray-500 mb-2 text-center">
                            This card stays in your list until you finish it.
                          </p>
                          <Button
                            variant="outline"
                            className="w-full h-12 border-green-500 text-green-700 hover:bg-green-50 font-semibold"
                            onClick={() => handleFinishCutting(true)}
                            disabled={updating}
                            data-testid="cutting-finish-btn"
                          >
                            <CheckCircle className="w-5 h-5 mr-2" /> Finish Cutting
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* BORING OPERATOR VIEW */}
              {user?.role === 'boring_operator' && (
                <div className="space-y-4">
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h3 className="font-medium mb-3">Mini Fix Hole Cutting Quantity</h3>
                    
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Current Quantity: <strong>{selectedJobCard.boring_qty_total || 0}</strong></Label>
                        <Input
                          type="number"
                          value={boringQuantity}
                          onChange={(e) => setBoringQuantity(e.target.value)}
                          placeholder="Enter quantity"
                          className="h-14 text-2xl text-center font-bold"
                          min="0"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Note (optional)</Label>
                        <Textarea
                          value={boringNote}
                          onChange={(e) => setBoringNote(e.target.value)}
                          placeholder="Any notes..."
                          rows={2}
                        />
                      </div>
                      
                      <Button 
                        className={`w-full h-12 bg-gradient-to-r ${getGradient()}`}
                        onClick={handleBoringQuantitySubmit}
                        disabled={updating || !boringQuantity}
                      >
                        {updating ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="w-5 h-5 mr-2" />
                            Update Quantity
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Finish / Reopen - the card stays pending until boring is finished */}
                  {boringStage(selectedJobCard) === 'completed' ? (
                    <div className="text-center">
                      <p className="text-sm text-green-700 font-medium mb-2">
                        <CheckCircle className="w-4 h-4 inline mr-1" />
                        Boring finished for this job card
                      </p>
                      <Button
                        variant="outline"
                        className="w-full h-11 border-amber-300 text-amber-700 hover:bg-amber-50"
                        onClick={() => handleFinishBoring(false)}
                        disabled={updating}
                        data-testid="boring-reopen-btn"
                      >
                        Reopen - more still to do
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <p className="text-xs text-gray-500 mb-2 text-center">
                        This card stays in your list until you finish it.
                      </p>
                      <Button
                        variant="outline"
                        className="w-full h-12 border-green-500 text-green-700 hover:bg-green-50 font-semibold"
                        onClick={() => handleFinishBoring(true)}
                        disabled={updating}
                        data-testid="boring-finish-btn"
                      >
                        <CheckCircle className="w-5 h-5 mr-2" /> Finish Boring
                      </Button>
                      <p className="text-[11px] text-gray-500 mt-1 text-center">Tap only when all minifix for this card is done.</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quick Stats */}
        {selectedJobCard && (
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-2 bg-blue-50 rounded">
                  <p className="text-2xl font-bold text-blue-600">{selectedJobCard.cutting_qty_total || 0}</p>
                  <p className="text-xs text-gray-600">Cutting</p>
                </div>
                <div className="p-2 bg-amber-50 rounded">
                  <p className="text-2xl font-bold text-amber-600">{selectedJobCard.pressing_qty_total || 0}</p>
                  <p className="text-xs text-gray-600">Pressing</p>
                </div>
                <div className="p-2 bg-purple-50 rounded">
                  <p className="text-2xl font-bold text-purple-600">{selectedJobCard.boring_qty_total || 0}</p>
                  <p className="text-xs text-gray-600">Boring</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* View Full Pressing Sheet Dialog (Coordinator's format - read only) */}
      <Dialog open={viewingPressingSheet} onOpenChange={setViewingPressingSheet}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="operator-view-pressing-sheet-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Pressing Sheet - Job Card #{selectedJobCard?.job_card_number}</DialogTitle>
              <Button
                variant="outline"
                size="sm"
                className="mr-6"
                onClick={() => selectedJobCard && printPressingJobCard(selectedJobCard)}
                data-testid="operator-view-sheet-print-btn"
              >
                <Printer className="w-4 h-4 mr-1" /> Print
              </Button>
            </div>
          </DialogHeader>
          {selectedJobCard && (
            <div className="border-2 border-gray-800 bg-white" id="pressing-sheet-dialog">
              {/* Header Section */}
              <div className="border-b-2 border-gray-800 p-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <label className="font-bold whitespace-nowrap text-xs">JOB CARD NO:</label>
                    <div className="flex-1 border-b-2 border-gray-800 px-2 py-1 text-sm bg-gray-50">{selectedJobCard.job_card_number}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="font-bold whitespace-nowrap text-xs">DATE:</label>
                    <div className="flex-1 border-b-2 border-gray-800 px-2 py-1 text-sm">
                      {selectedJobCard.created_at ? new Date(selectedJobCard.created_at).toLocaleDateString('en-IN') : '-'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="font-bold whitespace-nowrap text-xs">CUSTOMER:</label>
                    <div className="flex-1 border-b-2 border-gray-800 px-2 py-1 text-sm bg-gray-50">{selectedJobCard.customer_name}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="font-bold whitespace-nowrap text-xs">DELIVERY DATE:</label>
                    <div className="flex-1 border-b-2 border-gray-800 px-2 py-1 text-sm">
                      {selectedJobCard.delivery_date ? new Date(selectedJobCard.delivery_date).toLocaleDateString('en-IN') : '-'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="font-bold whitespace-nowrap text-xs">REFERENCE:</label>
                    <div className="flex-1 border-b-2 border-gray-800 px-2 py-1 text-sm">{selectedJobCard.reference || '-'}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="font-bold whitespace-nowrap text-xs">MOB. NO:</label>
                    <div className="flex-1 border-b-2 border-gray-800 px-2 py-1 text-sm">{selectedJobCard.mobile_no || '-'}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="font-bold whitespace-nowrap text-xs">REF J.C NO:</label>
                    <div className="flex-1 border-b-2 border-gray-800 px-2 py-1 text-sm">{selectedJobCard.ref_job_card_no || '-'}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="font-bold whitespace-nowrap text-xs">WORK IN-CHARGE:</label>
                    <div className="flex-1 border-b-2 border-gray-800 px-2 py-1 text-sm">{selectedJobCard.work_in_charge || '-'}</div>
                  </div>
                </div>
              </div>

              {/* NORMAL PRESSING DETAILS Section */}
              <div className="border-b-2 border-gray-800">
                <div className="bg-blue-100 p-2 font-bold border-b border-gray-800">
                  NORMAL PRESSING DETAILS
                </div>
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-400 p-2 w-12">SL. NO.</th>
                      <th className="border border-gray-400 p-2">NORMAL PRESSING</th>
                      <th className="border border-gray-400 p-2 w-16">PLY QTY</th>
                      <th className="border border-gray-400 p-2 w-14">SIDE</th>
                      <th className="border border-gray-400 p-2 w-14">TOTAL</th>
                      <th className="border border-gray-400 p-2 w-24">DETAILS</th>
                      <th className="border border-gray-400 p-2 w-20">STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...(selectedJobCard.normal_pressing_lines || []), ...(selectedJobCard.hot_pressing_lines || [])].length === 0 ? (
                      <tr>
                        <td colSpan="7" className="border border-gray-400 p-4 text-center text-gray-500">
                          No normal pressing details added
                        </td>
                      </tr>
                    ) : (
                      [...(selectedJobCard.normal_pressing_lines || []), ...(selectedJobCard.hot_pressing_lines || [])].map((line, idx) => (
                        <tr key={idx}>
                          <td className="border border-gray-400 p-2 text-center">{idx + 1}</td>
                          <td className="border border-gray-400 p-2">{line.description || '-'}</td>
                          <td className="border border-gray-400 p-2 text-center">{line.ply_qty || '-'}</td>
                          <td className="border border-gray-400 p-2 text-center">{line.side || '-'}</td>
                          <td className="border border-gray-400 p-2 text-center font-bold">{line.total || (line.ply_qty * line.side) || '-'}</td>
                          <td className="border border-gray-400 p-2 text-xs">{line.details || '-'}</td>
                          <td className="border border-gray-400 p-2 text-center">
                            <Badge className={line.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                              {line.status || 'pending'}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* PVC & ACRYLIC PRESSING DETAILS Section */}
              <div className="border-b-2 border-gray-800">
                <div className="bg-purple-100 p-2 font-bold border-b border-gray-800">
                  PVC & ACRYLIC PRESSING DETAILS
                </div>
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-400 p-2 w-12">SL. NO.</th>
                      <th className="border border-gray-400 p-2">PVC & ACRYLIC PRESSING</th>
                      <th className="border border-gray-400 p-2 w-16">PLY QTY</th>
                      <th className="border border-gray-400 p-2 w-14">SIDE</th>
                      <th className="border border-gray-400 p-2 w-14">TOTAL</th>
                      <th className="border border-gray-400 p-2 w-24">DETAILS</th>
                      <th className="border border-gray-400 p-2 w-20">STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedJobCard.veneer_pressing_lines || []).length === 0 ? (
                      <tr>
                        <td colSpan="7" className="border border-gray-400 p-4 text-center text-gray-500">
                          No PVC & Acrylic pressing details added
                        </td>
                      </tr>
                    ) : (
                      (selectedJobCard.veneer_pressing_lines || []).map((line, idx) => (
                        <tr key={idx}>
                          <td className="border border-gray-400 p-2 text-center">{idx + 1}</td>
                          <td className="border border-gray-400 p-2">{line.description || '-'}</td>
                          <td className="border border-gray-400 p-2 text-center">{line.ply_qty || '-'}</td>
                          <td className="border border-gray-400 p-2 text-center">{line.side || '-'}</td>
                          <td className="border border-gray-400 p-2 text-center font-bold">{line.total || (line.ply_qty * line.side) || '-'}</td>
                          <td className="border border-gray-400 p-2 text-xs">{line.details || '-'}</td>
                          <td className="border border-gray-400 p-2 text-center">
                            <Badge className={line.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                              {line.status || 'pending'}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Hot Pressing Diagrams/Images Section */}
              <div>
                <div className="bg-orange-100 p-2 font-bold border-b border-gray-800 flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" /> HOT PRESSING DIAGRAMS / IMAGES
                </div>
                {selectedJobCard.pressing_images?.length > 0 ? (
                  <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {selectedJobCard.pressing_images.map((url, idx) => (
                      <img 
                        key={idx}
                        src={`${API_BASE}${url}`} 
                        alt={`Pressing diagram ${idx + 1}`}
                        className="w-full h-40 object-contain rounded border cursor-pointer hover:opacity-90 transition-opacity bg-white"
                        onClick={() => setViewingPressingImage(url)}
                        data-testid={`operator-sheet-image-${idx}`}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center text-gray-500">
                    No diagrams attached.
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Full Pressing Image Dialog */}
      <Dialog open={!!viewingPressingImage} onOpenChange={() => setViewingPressingImage(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="operator-view-pressing-image-dialog">
          <DialogHeader>
            <DialogTitle>Pressing Diagram</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-2">
            {viewingPressingImage && (
              <img 
                src={`${API_BASE}${viewingPressingImage}`} 
                alt="Pressing diagram" 
                className="max-h-[60vh] w-auto rounded-lg object-contain"
                data-testid="operator-pressing-image-full"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OperatorDashboard;
